import React, { useState, useCallback, useEffect, useRef } from "react";
import styled from "styled-components";
import {
  appStudy,
  apiUrl,
  infoBaseUrl,
  backendVersion,
  nutritionixAcceptedLocales,
} from "../../../config.js";
import { availableLanguages } from "../../../i18n/helpers.js";

const OverlayContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 50px;
  z-index: 9999;
  color: white;
  font-size: 18px;
`;

const OverlayContent = styled.div`
  text-align: left;
  max-width: 80%;
`;

const OverlayTitle = styled.h2`
  color: white;
  font-size: 24px;
  margin-bottom: 20px;
  text-align: center;
`;

export const useOverlay = () => {
  const [show, setShow] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleLogoInteraction = useCallback((event) => {
    if (
      (event.type === "mousedown" || event.type === "pointerdown") &&
      (event.button !== 0 || event.buttons !== 1)
    ) {
      return;
    }

    event.persist();
    const eventType = event.type;
    const timer = setTimeout(() => {
      if (mountedRef.current) {
        setShow(true);
      }
    }, 3000);

    const handleEnd = () => {
      clearTimeout(timer);
      if (mountedRef.current) {
        setShow(false);
      }
      document.removeEventListener(
        eventType === "mousedown" ? "mouseup" : "touchend",
        handleEnd
      );
      if (eventType === "mousedown") {
        document.removeEventListener("mousemove", handleEnd);
      }
    };

    document.addEventListener(
      eventType === "mousedown" ? "mouseup" : "touchend",
      handleEnd,
      { once: true }
    );
    if (eventType === "mousedown") {
      document.addEventListener("mousemove", handleEnd, { once: true });
    }

    return () => {
      clearTimeout(timer);
      document.removeEventListener(
        eventType === "mousedown" ? "mouseup" : "touchend",
        handleEnd
      );
      if (eventType === "mousedown") {
        document.removeEventListener("mousemove", handleEnd);
      }
    };
  }, []);

  return { show, handleLogoInteraction };
};

export const Overlay = ({ show }) => {
  const [apiUrlValue, setApiUrlValue] = useState("");
  const [infoBaseUrlValue, setInfoBaseUrlValue] = useState("");
  const [backendVersionValue, setBackendVersionValue] = useState("");
  const [internetTime, setInternetTime] = useState("");
  const [nutritionixLocales, setNutritionixLocales] = useState([]);

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        if (mounted) {
          const [url, info, version, locales] = await Promise.allSettled([
            apiUrl(),
            infoBaseUrl(),
            backendVersion(),
            nutritionixAcceptedLocales(),
          ]);

          setApiUrlValue(url.status === "fulfilled" ? url.value : "");
          setInfoBaseUrlValue(info.status === "fulfilled" ? info.value : "");
          setBackendVersionValue(
            version.status === "fulfilled" ? version.value : ""
          );
          setNutritionixLocales(
            locales.status === "fulfilled" ? locales.value : []
          );
        }
      } catch (error) {
        if (mounted) {
          setApiUrlValue("");
          setInfoBaseUrlValue("");
          setBackendVersionValue("");
          setNutritionixLocales([]);
        }
      }
    };

    if (show) {
      fetchData();
    }

    return () => {
      mounted = false;
    };
  }, [show]);

  useEffect(() => {
    if (show) {
      fetch("https://worldtimeapi.org/api/ip")
        .then((response) => response.json())
        .then((data) => {
          const utcTime = new Date(data.utc_datetime);
          setInternetTime(
            utcTime.toLocaleString("en-GB", {
              weekday: "short",
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: false,
              timeZone: "UTC",
              timeZoneName: "short",
            })
          );
        })
        .catch(() => setInternetTime("Unable to fetch"));
    }
  }, [show]);

  if (!show) return null;

  return (
    <OverlayContainer>
      <OverlayContent>
        <OverlayTitle>System Information</OverlayTitle>
        <table style={{ borderCollapse: "collapse" }}>
          <tbody>
            <tr>
              <td>
                <strong>Browser Date/Time:</strong>
              </td>
              <td>
                <span data-testid="current-datetime">
                  {(() => {
                    const now = new Date();
                    const offset = -now.getTimezoneOffset() / 60;
                    const offsetStr = offset >= 0 ? `+${offset}` : `${offset}`;
                    return `${now.toLocaleString("en-GB", {
                      weekday: "short",
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                      hour12: false,
                      timeZoneName: "long",
                    })} (UTC${offsetStr})`;
                  })()}
                </span>
              </td>
            </tr>
            <tr>
              <td>
                <strong>Internet Date/Time:</strong>
              </td>
              <td>
                <span data-testid="current-datetime">{internetTime}</span>
              </td>
            </tr>
            <tr>
              <td>
                <strong>App Study:</strong>
              </td>
              <td>
                <span data-testid="app-study">{appStudy}</span>
              </td>
            </tr>
            <tr>
              <td>
                <strong>Available Languages:</strong>
              </td>
              <td>
                <span data-testid="available-languages">
                  {availableLanguages}
                </span>
              </td>
            </tr>
            <tr>
              <td>
                <strong>Food Database Accepted Locales:</strong>
              </td>
              <td>
                <span data-testid="food-database-accepted-locales">
                  {nutritionixLocales.join(", ")}
                </span>
              </td>
            </tr>
            <tr>
              <td>
                <strong>Frontend Host:</strong>
              </td>
              <td>
                <span data-testid="runtime-host">
                  {window.location.hostname || "Unknown"}
                </span>
              </td>
            </tr>
            <tr>
              <td>
                <strong>Frontend Version:</strong>
              </td>
              <td>
                <span data-testid="frontend-version">
                  {process.env.REACT_APP_VERSION}
                </span>
              </td>
            </tr>
            <tr>
              <td>
                <strong>Backend Version:</strong>
              </td>
              <td>
                <span data-testid="backend-version">{backendVersionValue}</span>
              </td>
            </tr>
            <tr>
              <td>
                <strong>Backend API URL:</strong>
              </td>
              <td>
                <span data-testid="api-url">{apiUrlValue}</span>
              </td>
            </tr>
            <tr>
              <td>
                <strong>Info Base URL:</strong>
              </td>
              <td>
                <span data-testid="info-base-url">{infoBaseUrlValue}</span>
              </td>
            </tr>
            <tr>
              <td>
                <strong>Build Descriptor:</strong>
              </td>
              <td>
                <span data-testid="build-descriptor">
                  {process?.env?.REACT_APP_BUILD_DESCRIPTOR}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </OverlayContent>
    </OverlayContainer>
  );
};
